// Derive a display first-name from a Supabase auth user. Falls back to
// null when the metadata is missing AND the email handle does not look
// like a person's name. Both WeekHome's greeting and BodyLetter's
// salutation use this so they're consistent.
//
// The previous per-component versions stripped at [._-] but did not
// reject obviously-non-name handles. For an account like
// `pw-test@example.com`, the old logic produced "Pw-test" or "Pw" in
// the greeting, which reads as broken. This helper detects the common
// cases (too-short, all-digits, generic mailbox names) and returns null
// instead, letting the caller render its generic-greeting fallback.

const GENERIC_HANDLES = new Set([
  'admin', 'info', 'hello', 'hi', 'contact', 'support', 'help',
  'test', 'demo', 'user', 'me', 'mail', 'email', 'team', 'noreply',
  'no-reply', 'office', 'service', 'sales', 'billing', 'pw',
]);

export function firstNameFromUser(user) {
  if (!user) return null;
  const md = user.user_metadata ?? {};

  // 1. Trust an explicit first-name / display-name from the auth
  //    provider when one exists. Take only the first whitespace-
  //    separated token so "Paula Silva" -> "Paula".
  const explicit = md.first_name || md.full_name || md.name || md.display_name;
  if (explicit) {
    const first = String(explicit).trim().split(/\s+/)[0];
    if (first) return capitalise(first);
  }

  // 2. Otherwise try the email's local-part. Strip at the first
  //    [._-] so "paula.tomazetti" -> "paula", and drop trailing digits
  //    so "paula123" -> "paula".
  const email = user.email || '';
  const handle = email.split('@')[0] || '';
  if (!handle) return null;
  const cleaned = handle.split(/[._-]/)[0].replace(/\d+$/, '');
  if (!cleaned) return null;
  const lower = cleaned.toLowerCase();

  // 3. Reject obviously-non-name handles. Returning null lets the
  //    caller fall back to a generic greeting, which is a better UX
  //    than "Welcome back, Admin." or "Dear Pw,"
  if (lower.length < 3) return null;
  if (GENERIC_HANDLES.has(lower)) return null;
  if (/\d/.test(lower)) return null;

  return capitalise(cleaned);
}

function capitalise(s) {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}
